(() => {
  'use strict';

  const CONFIG = Object.freeze({
    storageKey: 'lcm_vehicle_state_v1',
    version: 1,

    activeVehicleTtlDays: 7,
    garageMaxVehicles: 3,

    makeFilterParam:
      'filter.p.m.custom.car_make',

    modelFilterParam:
      'filter.p.m.custom.car_model',

    universalValue:
      'Universal'
  });


  const DAY_MS =
    24 * 60 * 60 * 1000;


  /*
   * =========================================================
   * STATE
   * =========================================================
   */

  const emptyState = () => ({
    version: CONFIG.version,
    activeVehicle: null,
    garage: []
  });


  const normalizeVehicle = (
    vehicle
  ) => {

    if (
      !vehicle ||
      typeof vehicle !== 'object'
    ) {
      return null;
    }


    const make =
      String(
        vehicle.make || ''
      ).trim();


    const model =
      String(
        vehicle.model || ''
      ).trim();


    if (
      !make ||
      !model
    ) {
      return null;
    }


    return {
      make,
      model
    };
  };


  const vehicleId = (
    vehicle
  ) => {

    const normalized =
      normalizeVehicle(
        vehicle
      );


    if (!normalized) {
      return '';
    }


    return (
      normalized.make +
      '::' +
      normalized.model
    ).toLowerCase();
  };


  const sameVehicle = (
    a,
    b
  ) => {

    const aId =
      vehicleId(a);

    const bId =
      vehicleId(b);


    return (
      aId !== '' &&
      aId === bId
    );
  };


  /*
   * =========================================================
   * STORAGE
   * =========================================================
   */

  const safelyReadStorage = () => {

    try {

      return window.localStorage.getItem(
        CONFIG.storageKey
      );

    } catch (error) {

      console.warn(
        '[LCM Vehicle] localStorage unavailable.',
        error
      );

      return null;
    }
  };


  const safelyWriteStorage = (
    value
  ) => {

    try {

      window.localStorage.setItem(
        CONFIG.storageKey,
        JSON.stringify(value)
      );

      return true;

    } catch (error) {

      console.warn(
        '[LCM Vehicle] Could not save state.',
        error
      );

      return false;
    }
  };


  const safelyRemoveStorage = () => {

    try {

      window.localStorage.removeItem(
        CONFIG.storageKey
      );

    } catch (error) {

      console.warn(
        '[LCM Vehicle] Could not clear state.',
        error
      );
    }
  };


  /*
   * =========================================================
   * GARAGE
   * =========================================================
   */

  const normalizeGarage = (
    garage
  ) => {

    if (
      !Array.isArray(
        garage
      )
    ) {
      return [];
    }


    const uniqueVehicles =
      new Map();


    garage.forEach(
      (item) => {

        const vehicle =
          normalizeVehicle(
            item
          );


        if (!vehicle) {
          return;
        }


        const lastUsedAt =
          Number(
            item.lastUsedAt
          ) || 0;


        const id =
          vehicleId(
            vehicle
          );


        const existing =
          uniqueVehicles.get(
            id
          );


        if (
          !existing ||
          lastUsedAt >
            existing.lastUsedAt
        ) {

          uniqueVehicles.set(
            id,
            {
              ...vehicle,
              lastUsedAt
            }
          );
        }
      }
    );


    return Array
      .from(
        uniqueVehicles.values()
      )
      .sort(
        (a, b) =>
          b.lastUsedAt -
          a.lastUsedAt
      )
      .slice(
        0,
        CONFIG.garageMaxVehicles
      );
  };


  /*
   * =========================================================
   * READ STATE
   * =========================================================
   */

  const readState = () => {

    const raw =
      safelyReadStorage();


    if (!raw) {
      return emptyState();
    }


    try {

      const parsed =
        JSON.parse(
          raw
        );


      if (
        !parsed ||
        typeof parsed !==
          'object' ||
        parsed.version !==
          CONFIG.version
      ) {
        return emptyState();
      }


      let activeVehicle =
        null;


      if (
        parsed.activeVehicle
      ) {

        const vehicle =
          normalizeVehicle(
            parsed.activeVehicle
          );


        if (vehicle) {

          activeVehicle = {
            ...vehicle,

            selectedAt:
              Number(
                parsed.activeVehicle
                  .selectedAt
              ) ||
              Date.now(),

            lastUsedAt:
              Number(
                parsed.activeVehicle
                  .lastUsedAt
              ) || 0
          };
        }
      }


      return {
        version:
          CONFIG.version,

        activeVehicle,

        garage:
          normalizeGarage(
            parsed.garage
          )
      };

    } catch (error) {

      console.warn(
        '[LCM Vehicle] Invalid saved state ignored.',
        error
      );


      return emptyState();
    }
  };


  let state =
    readState();


  const saveState = () => {

    safelyWriteStorage(
      state
    );
  };


  /*
   * =========================================================
   * EVENTS
   * =========================================================
   */

  const dispatchChange = (
    reason = 'update'
  ) => {

    window.dispatchEvent(
      new CustomEvent(
        'lcm:vehiclechange',
        {
          detail: {
            reason,

            activeVehicle:
              state.activeVehicle
                ? {
                    ...state.activeVehicle
                  }
                : null,

            garage:
              state.garage.map(
                (vehicle) => ({
                  ...vehicle
                })
              )
          }
        }
      )
    );
  };


  /*
   * =========================================================
   * TTL
   * =========================================================
   */

  const activeVehicleHasExpired =
    () => {

      if (
        !state.activeVehicle
      ) {
        return false;
      }


      const lastUsedAt =
        Number(
          state.activeVehicle
            .lastUsedAt
        );


      if (!lastUsedAt) {
        return true;
      }


      const ttl =
        CONFIG
          .activeVehicleTtlDays *
        DAY_MS;


      return (
        Date.now() -
          lastUsedAt >
        ttl
      );
    };


  const expireActiveVehicleIfNeeded =
    () => {

      if (
        !activeVehicleHasExpired()
      ) {
        return false;
      }


      state.activeVehicle =
        null;


      saveState();


      dispatchChange(
        'expired'
      );


      return true;
    };


  const touchActiveVehicle =
    () => {

      if (
        !state.activeVehicle
      ) {
        return;
      }


      const now =
        Date.now();


      state.activeVehicle
        .lastUsedAt =
        now;


      const garageVehicle =
        state.garage.find(
          (vehicle) =>
            sameVehicle(
              vehicle,
              state.activeVehicle
            )
        );


      if (
        garageVehicle
      ) {

        garageVehicle
          .lastUsedAt =
          now;


        state.garage =
          normalizeGarage(
            state.garage
          );
      }


      saveState();
    };


  /*
   * =========================================================
   * VEHICLE SELECTION
   * =========================================================
   */

  const addToGarage = (
    vehicle,
    timestamp =
      Date.now()
  ) => {

    const normalized =
      normalizeVehicle(
        vehicle
      );


    if (!normalized) {
      return;
    }


    const withoutVehicle =
      state.garage.filter(
        (garageVehicle) =>
          !sameVehicle(
            garageVehicle,
            normalized
          )
      );


    state.garage =
      normalizeGarage([
        {
          ...normalized,
          lastUsedAt:
            timestamp
        },

        ...withoutVehicle
      ]);
  };


  const selectVehicle = (
    vehicle,
    options = {}
  ) => {

    const normalized =
      normalizeVehicle(
        vehicle
      );


    if (!normalized) {

      console.warn(
        '[LCM Vehicle] Invalid vehicle selection.',
        vehicle
      );

      return false;
    }


    const now =
      Date.now();


    state.activeVehicle = {
      ...normalized,

      selectedAt:
        now,

      lastUsedAt:
        now
    };


    addToGarage(
      normalized,
      now
    );


    saveState();


    dispatchChange(
      options.reason ||
      'selected'
    );


    return true;
  };


  const deselectVehicle = (
    options = {}
  ) => {

    if (
      !state.activeVehicle
    ) {
      return false;
    }


    state.activeVehicle =
      null;


    saveState();


    dispatchChange(
      options.reason ||
      'deselected'
    );


    return true;
  };


  const removeVehicleFromGarage =
    (
      vehicle
    ) => {

      const normalized =
        normalizeVehicle(
          vehicle
        );


      if (!normalized) {
        return false;
      }


      const oldLength =
        state.garage.length;


      state.garage =
        state.garage.filter(
          (garageVehicle) =>
            !sameVehicle(
              garageVehicle,
              normalized
            )
        );


      if (
        sameVehicle(
          state.activeVehicle,
          normalized
        )
      ) {

        state.activeVehicle =
          null;
      }


      const changed =
        state.garage.length !==
        oldLength;


      if (changed) {

        saveState();


        dispatchChange(
          'garage-remove'
        );
      }


      return changed;
    };


  /*
   * =========================================================
   * GETTERS
   * =========================================================
   */

  const getActiveVehicle =
    () => {

      expireActiveVehicleIfNeeded();


      return state.activeVehicle
        ? {
            ...state.activeVehicle
          }
        : null;
    };


  const getGarage = () => {

    return state.garage.map(
      (vehicle) => ({
        ...vehicle
      })
    );
  };


  const getState = () => {

    expireActiveVehicleIfNeeded();


    return {
      version:
        state.version,

      activeVehicle:
        state.activeVehicle
          ? {
              ...state.activeVehicle
            }
          : null,

      garage:
        getGarage()
    };
  };


  /*
   * =========================================================
   * COLLECTION URL HELPERS
   * =========================================================
   */

  const isCollectionPath = (
    pathname
  ) => {

    return /^\/(?:[a-zA-Z]{2}(?:-[a-zA-Z]{2})?\/)?collections(?:\/|$)/.test(
      pathname
    );
  };


  const isCollectionUrl = (
    url
  ) => {

    try {

      const parsedUrl =
        new URL(
          url,
          window.location.origin
        );


      return (
        parsedUrl.origin ===
          window.location.origin &&
        isCollectionPath(
          parsedUrl.pathname
        )
      );

    } catch (error) {

      return false;
    }
  };


  const formatInternalUrl = (
    parsedUrl
  ) => {

    return (
      parsedUrl.pathname +
      parsedUrl.search +
      parsedUrl.hash
    );
  };


  const hasVehicleFilter = (
    url
  ) => {

    try {

      const parsedUrl =
        new URL(
          url,
          window.location.origin
        );


      return (
        parsedUrl.searchParams.has(
          CONFIG.makeFilterParam
        ) ||
        parsedUrl.searchParams.has(
          CONFIG.modelFilterParam
        )
      );

    } catch (error) {

      return false;
    }
  };


  const getFilteredCollectionUrl =
    (
      url,
      vehicle =
        getActiveVehicle()
    ) => {

      if (
        !vehicle ||
        !isCollectionUrl(
          url
        )
      ) {
        return url;
      }


      const parsedUrl =
        new URL(
          url,
          window.location.origin
        );


      /*
       * Geen make-filter gebruiken.
       *
       * Universal producten hebben niet noodzakelijk
       * Mazda / Volvo / BMW als make.
       */
      parsedUrl.searchParams.delete(
        CONFIG.makeFilterParam
      );


      parsedUrl.searchParams.delete(
        CONFIG.modelFilterParam
      );


      /*
       * Model OR Universal.
       */
      parsedUrl.searchParams.append(
        CONFIG.modelFilterParam,
        vehicle.model
      );


      parsedUrl.searchParams.append(
        CONFIG.modelFilterParam,
        CONFIG.universalValue
      );


      return formatInternalUrl(
        parsedUrl
      );
    };


  const getUnfilteredCollectionUrl =
    (
      url
    ) => {

      if (
        !isCollectionUrl(
          url
        )
      ) {
        return url;
      }


      const parsedUrl =
        new URL(
          url,
          window.location.origin
        );


      parsedUrl.searchParams.delete(
        CONFIG.makeFilterParam
      );


      parsedUrl.searchParams.delete(
        CONFIG.modelFilterParam
      );


      /*
       * Andere filters en sort_by blijven intact.
       */
      return formatInternalUrl(
        parsedUrl
      );
    };


  /*
   * =========================================================
   * COLLECTION LINKS
   * =========================================================
   */

  const getCollectionLinkDestination =
    (
      link
    ) => {

      if (
        !link ||
        !link.href
      ) {
        return null;
      }


      let destination;


      try {

        destination =
          new URL(
            link.href,
            window.location.origin
          );

      } catch (error) {

        return null;
      }


      if (
        destination.origin !==
          window.location.origin ||
        !isCollectionPath(
          destination.pathname
        )
      ) {
        return null;
      }


      return destination;
    };


  const getVehicleAwareLinkUrl =
    (
      link
    ) => {

      const destination =
        getCollectionLinkDestination(
          link
        );


      if (!destination) {
        return null;
      }


      const activeVehicle =
        getActiveVehicle();


      if (!activeVehicle) {
        return null;
      }


      /*
       * Bestaande / legacy filterlinks
       * nooit herschrijven.
       */
      if (
        destination
          .searchParams
          .has(
            CONFIG
              .makeFilterParam
          ) ||
        destination
          .searchParams
          .has(
            CONFIG
              .modelFilterParam
          )
      ) {
        return null;
      }


      return getFilteredCollectionUrl(
        destination.href,
        activeVehicle
      );
    };


  /*
   * =========================================================
   * NORMAL CLICK / CMD / CTRL
   * =========================================================
   */

  const handleCollectionLinkClick =
    (
      event
    ) => {

      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.altKey
      ) {
        return;
      }


      const link =
        event.target.closest(
          'a[href]'
        );


      if (
        !link ||
        link.hasAttribute(
          'download'
        )
      ) {
        return;
      }


      const filteredUrl =
        getVehicleAwareLinkUrl(
          link
        );


      if (!filteredUrl) {
        return;
      }


      if (
        event.metaKey ||
        event.ctrlKey ||
        link.target ===
          '_blank'
      ) {

        event.preventDefault();


        window.open(
          filteredUrl,
          '_blank',
          'noopener'
        );


        return;
      }


      if (
        event.shiftKey
      ) {
        return;
      }


      event.preventDefault();


      window.location.assign(
        filteredUrl
      );
    };


  document.addEventListener(
    'click',
    handleCollectionLinkClick
  );


  /*
   * =========================================================
   * MIDDLE CLICK
   * =========================================================
   */

  const handleCollectionAuxClick =
    (
      event
    ) => {

      if (
        event.defaultPrevented ||
        event.button !== 1
      ) {
        return;
      }


      const link =
        event.target.closest(
          'a[href]'
        );


      if (
        !link ||
        link.hasAttribute(
          'download'
        )
      ) {
        return;
      }


      const filteredUrl =
        getVehicleAwareLinkUrl(
          link
        );


      if (!filteredUrl) {
        return;
      }


      event.preventDefault();


      window.open(
        filteredUrl,
        '_blank',
        'noopener'
      );
    };


  document.addEventListener(
    'auxclick',
    handleCollectionAuxClick
  );


  /*
   * =========================================================
   * RIGHT CLICK / CONTEXT MENU
   * =========================================================
   */

  const contextMenuOriginalHrefs =
    new WeakMap();


  const restoreContextMenuHref =
    (
      link
    ) => {

      if (
        !contextMenuOriginalHrefs
          .has(
            link
          )
      ) {
        return;
      }


      const originalHref =
        contextMenuOriginalHrefs
          .get(
            link
          );


      link.setAttribute(
        'href',
        originalHref
      );


      contextMenuOriginalHrefs
        .delete(
          link
        );
    };


  const handleCollectionContextMenu =
    (
      event
    ) => {

      const link =
        event.target.closest(
          'a[href]'
        );


      if (
        !link ||
        link.hasAttribute(
          'download'
        )
      ) {
        return;
      }


      const filteredUrl =
        getVehicleAwareLinkUrl(
          link
        );


      if (!filteredUrl) {
        return;
      }


      if (
        !contextMenuOriginalHrefs
          .has(
            link
          )
      ) {

        contextMenuOriginalHrefs
          .set(
            link,
            link.getAttribute(
              'href'
            )
          );
      }


      link.setAttribute(
        'href',
        filteredUrl
      );


      window.setTimeout(
        () => {

          restoreContextMenuHref(
            link
          );

        },
        60000
      );
    };


  document.addEventListener(
    'contextmenu',
    handleCollectionContextMenu
  );


  document.addEventListener(
    'pointerdown',
    (
      event
    ) => {

      const link =
        event.target.closest(
          'a[href]'
        );


      if (
        !link ||
        event.button === 2
      ) {
        return;
      }


      restoreContextMenuHref(
        link
      );
    },
    true
  );


  /*
   * =========================================================
   * EXPLICIT LCM VEHICLE PICKER SOURCE
   * =========================================================
   *
   * De juiste Ignite Quick Filter wordt NIET meer
   * herkend op positie, labels of metafield-options.
   *
   * quick-filter.liquid markeert hem server-side met:
   *
   * data-lcm-vehicle-picker-source
   *
   * Daardoor is er maar één bron van waarheid.
   * =========================================================
   */

  const getConfiguredVehiclePicker =
    () => {

      const source =
        document.querySelector(
          '[data-lcm-vehicle-picker-source]'
        );


      if (!source) {
        return null;
      }


      const quickFilter =
        source.querySelector(
          'quick-filter[data-mode="filter"]'
        );


      if (!quickFilter) {
        return null;
      }


      return {
        source,
        quickFilter
      };
    };


  /*
   * =========================================================
   * MOUNT PICKER INSIDE VEHICLE STATUS
   * =========================================================
   */

  const mountVehiclePicker = (
    statusWrapper
  ) => {

    if (!statusWrapper) {
      return null;
    }


    const mount =
      statusWrapper.querySelector(
        '[data-lcm-vehicle-picker-mount]'
      );


    if (!mount) {
      return null;
    }


    /*
     * Is de native Ignite Quick Filter al gemonteerd?
     */
    const existing =
      mount.querySelector(
        'quick-filter[data-mode="filter"]'
      );


    if (existing) {
      return existing;
    }


    /*
     * Pak uitsluitend de expliciet gemarkeerde
     * Quick Filter-section.
     */
    const configured =
      getConfiguredVehiclePicker();


    if (!configured) {
      return null;
    }


    const source =
      configured.source;


    const quickFilter =
      configured.quickFilter;


    /*
     * De originele bron blijft permanent verborgen.
     *
     * Liquid doet dit al voordat de pagina wordt getekend,
     * deze regels zijn alleen extra bescherming.
     */
    source.hidden =
      true;


    source.setAttribute(
      'aria-hidden',
      'true'
    );


    source.style.setProperty(
      'display',
      'none',
      'important'
    );


    /*
     * Belangrijk:
     *
     * We CLONEN de Ignite selector niet.
     * We verplaatsen exact dezelfde bestaande component.
     *
     * Daarmee blijven:
     * - nice-select
     * - dropdown dependencies
     * - quick-filter.js
     * - collection filtering
     *
     * gewoon van Ignite zelf.
     */
    mount.appendChild(
      quickFilter
    );


    /*
     * De geïntegreerde selector begint altijd gesloten.
     */
    mount.hidden =
      true;


    return quickFilter;
  };


  /*
   * =========================================================
   * VEHICLE STATUS
   * =========================================================
   */

  const vehicleLabel = (
    vehicle
  ) => {

    if (!vehicle) {
      return '';
    }


    return (
      vehicle.make +
      ' ' +
      vehicle.model
    );
  };


  /*
   * =========================================================
   * OPEN / CLOSE INTEGRATED PICKER
   * =========================================================
   *
   * Er is nu maar één state:
   *
   * mount.hidden
   *
   * Geen combinatie meer van:
   * - source section hidden
   * - CSS classes
   * - aria-expanded
   * - sibling status
   *
   * Daardoor werkt:
   *
   * open → dicht → open → dicht
   *
   * onbeperkt.
   * =========================================================
   */

  const setPickerOpen = (
    statusWrapper,
    open
  ) => {

    if (!statusWrapper) {
      return false;
    }


    const quickFilter =
      mountVehiclePicker(
        statusWrapper
      );


    if (!quickFilter) {
      return false;
    }


    const mount =
      statusWrapper.querySelector(
        '[data-lcm-vehicle-picker-mount]'
      );


    if (!mount) {
      return false;
    }


    mount.hidden =
      !open;


    statusWrapper.classList.toggle(
      'is-picker-open',
      open
    );


    /*
     * Er is maximaal één zichtbare toggle,
     * maar we werken beide states bij zodat
     * active/inactive altijd consistent blijven.
     */
    statusWrapper
      .querySelectorAll(
        '[data-lcm-toggle-picker]'
      )
      .forEach(
        (
          toggle
        ) => {

          toggle.setAttribute(
            'aria-expanded',
            open
              ? 'true'
              : 'false'
          );


          const label =
            toggle.querySelector(
              '[data-lcm-toggle-picker-label]'
            );


          if (!label) {
            return;
          }


          label.textContent =
            open
              ? toggle.dataset
                  .labelClose
              : toggle.dataset
                  .labelOpen;
        }
      );


    return true;
  };


  /*
   * =========================================================
   * RENDER VEHICLE STATUS
   * =========================================================
   */

  const renderVehicleStatus =
    () => {

      const wrappers =
        document.querySelectorAll(
          '[data-lcm-vehicle-status]'
        );


      if (
        !wrappers.length
      ) {
        return;
      }


      const activeVehicle =
        getActiveVehicle();


      const garage =
        getGarage();


      const recentVehicle =
        garage.length
          ? garage[0]
          : null;


      wrappers.forEach(
        (
          wrapper
        ) => {

          /*
           * Statusblok zichtbaar maken.
           */
          wrapper.hidden =
            false;


          /*
           * Native Ignite picker éénmalig in
           * de statuskaart plaatsen.
           */
          mountVehiclePicker(
            wrapper
          );


          /*
           * Iedere nieuwe paginalaad begint dicht.
           */
          setPickerOpen(
            wrapper,
            false
          );


          const activeState =
            wrapper.querySelector(
              '[data-lcm-active-state]'
            );


          const inactiveState =
            wrapper.querySelector(
              '[data-lcm-inactive-state]'
            );


          const activeVehicleText =
            wrapper.querySelector(
              '[data-lcm-active-vehicle]'
            );


          const recentWrapper =
            wrapper.querySelector(
              '[data-lcm-recent-vehicle-wrapper]'
            );


          const recentVehicleText =
            wrapper.querySelector(
              '[data-lcm-recent-vehicle]'
            );


          /*
           * ===================================================
           * ACTIVE VEHICLE
           * ===================================================
           */

          if (
            activeVehicle
          ) {

            if (
              activeState
            ) {
              activeState.hidden =
                false;
            }


            if (
              inactiveState
            ) {
              inactiveState.hidden =
                true;
            }


            if (
              activeVehicleText
            ) {

              activeVehicleText.textContent =
                vehicleLabel(
                  activeVehicle
                );
            }


            return;
          }


          /*
           * ===================================================
           * NO ACTIVE VEHICLE
           * ===================================================
           */

          if (
            activeState
          ) {
            activeState.hidden =
              true;
          }


          if (
            inactiveState
          ) {
            inactiveState.hidden =
              false;
          }


          /*
           * Laat laatst gebruikte garage-auto zien.
           */
          if (
            recentVehicle &&
            recentWrapper &&
            recentVehicleText
          ) {

            recentWrapper.hidden =
              false;


            recentVehicleText.textContent =
              vehicleLabel(
                recentVehicle
              );


            recentWrapper.dataset.make =
              recentVehicle.make;


            recentWrapper.dataset.model =
              recentVehicle.model;

          } else if (
            recentWrapper
          ) {

            recentWrapper.hidden =
              true;
          }
        }
      );
    };


  /*
   * =========================================================
   * WIJZIG AUTO / KIES AUTO / SLUITEN
   * =========================================================
   */

  document.addEventListener(
    'click',
    (
      event
    ) => {

      const button =
        event.target.closest(
          '[data-lcm-toggle-picker]'
        );


      if (!button) {
        return;
      }


      const wrapper =
        button.closest(
          '[data-lcm-vehicle-status]'
        );


      if (!wrapper) {
        return;
      }


      event.preventDefault();


      /*
       * Zorg dat picker gemonteerd is.
       */
      mountVehiclePicker(
        wrapper
      );


      const mount =
        wrapper.querySelector(
          '[data-lcm-vehicle-picker-mount]'
        );


      if (!mount) {
        return;
      }


      /*
       * De hidden-state van de mount is
       * de enige bron van waarheid.
       */
      const shouldOpen =
        mount.hidden;


      setPickerOpen(
        wrapper,
        shouldOpen
      );
    }
  );


  /*
   * =========================================================
   * WIS AUTO & TOON VOLLEDIG ASSORTIMENT
   * =========================================================
   */

  document.addEventListener(
    'click',
    (
      event
    ) => {

      const button =
        event.target.closest(
          '[data-lcm-show-full-catalog]'
        );


      if (!button) {
        return;
      }


      event.preventDefault();


      const destination =
        getUnfilteredCollectionUrl(
          window.location.href
        );


      /*
       * Alleen activeVehicle wissen.
       *
       * Garage blijft behouden.
       */
      deselectVehicle({
        reason:
          'show-full-catalog'
      });


      window.location.assign(
        destination
      );
    }
  );


  /*
   * =========================================================
   * RECENT VEHICLE RESELECT
   * =========================================================
   */

  document.addEventListener(
    'click',
    (
      event
    ) => {

      const button =
        event.target.closest(
          '[data-lcm-select-recent], [data-lcm-recent-vehicle]'
        );


      if (!button) {
        return;
      }


      const wrapper =
        button.closest(
          '[data-lcm-recent-vehicle-wrapper]'
        );


      if (!wrapper) {
        return;
      }


      const make =
        wrapper.dataset.make;


      const model =
        wrapper.dataset.model;


      if (
        !make ||
        !model
      ) {
        return;
      }


      event.preventDefault();


      selectVehicle(
        {
          make,
          model
        },
        {
          reason:
            'recent-selected'
        }
      );


      const destination =
        getFilteredCollectionUrl(
          window.location.href,
          {
            make,
            model
          }
        );


      window.location.assign(
        destination
      );
    }
  );


  /*
   * =========================================================
   * DYNAMIC VEHICLE TEXT
   * =========================================================
   *
   * Later te gebruiken voor bijvoorbeeld:
   *
   * Schroefsets voor jouw [MX-5 NA]
   * =========================================================
   */

  const updateDynamicVehicleText =
    () => {

      const activeVehicle =
        getActiveVehicle();


      document
        .querySelectorAll(
          '[data-lcm-vehicle-model]'
        )
        .forEach(
          (
            element
          ) => {

            /*
             * Server-side standaardtekst bewaren
             * zodat NL / EN / DE intact blijven.
             */
            if (
              !element.dataset
                .lcmDefaultText
            ) {

              element.dataset
                .lcmDefaultText =
                element.textContent;
            }


            element.textContent =
              activeVehicle
                ? activeVehicle.model
                : element.dataset
                    .lcmDefaultText;
          }
        );


      document
        .querySelectorAll(
          '[data-lcm-vehicle-make]'
        )
        .forEach(
          (
            element
          ) => {

            if (
              !element.dataset
                .lcmDefaultText
            ) {

              element.dataset
                .lcmDefaultText =
                element.textContent;
            }


            element.textContent =
              activeVehicle
                ? activeVehicle.make
                : element.dataset
                    .lcmDefaultText;
          }
        );
    };


  /*
   * =========================================================
   * RESET
   * =========================================================
   */

  const reset = () => {

    state =
      emptyState();


    safelyRemoveStorage();


    dispatchChange(
      'reset'
    );
  };


  /*
   * =========================================================
   * VEHICLE CHANGE
   * =========================================================
   */

  window.addEventListener(
    'lcm:vehiclechange',
    () => {

      renderVehicleStatus();

      updateDynamicVehicleText();
    }
  );


  /*
   * =========================================================
   * MULTIPLE TABS
   * =========================================================
   */

  window.addEventListener(
    'storage',
    (
      event
    ) => {

      if (
        event.key !==
        CONFIG.storageKey
      ) {
        return;
      }


      state =
        readState();


      expireActiveVehicleIfNeeded();


      dispatchChange(
        'external-update'
      );
    }
  );


  /*
   * =========================================================
   * INITIAL LOAD
   * =========================================================
   */

  expireActiveVehicleIfNeeded();


  touchActiveVehicle();


  const initializeUi =
    () => {

      renderVehicleStatus();

      updateDynamicVehicleText();
    };


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initializeUi
    );

  } else {

    initializeUi();
  }


  /*
   * =========================================================
   * SHOPIFY THEME EDITOR
   * =========================================================
   *
   * Wanneer sections opnieuw geladen worden in de
   * Theme Editor opnieuw initialiseren.
   * =========================================================
   */

  document.addEventListener(
    'shopify:section:load',
    () => {

      window.setTimeout(
        initializeUi,
        0
      );
    }
  );


  /*
   * =========================================================
   * PUBLIC API
   * =========================================================
   */

  window.LCMVehicle =
    Object.freeze({

      select:
        selectVehicle,

      deselect:
        deselectVehicle,

      removeFromGarage:
        removeVehicleFromGarage,

      getActive:
        getActiveVehicle,

      getGarage,

      getState,

      getFilteredCollectionUrl,

      getUnfilteredCollectionUrl,

      isCollectionUrl,

      hasVehicleFilter,

      touch:
        touchActiveVehicle,

      reset,

      sameVehicle
    });

})();